import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { t } from '../lib/i18n';
import { messageFor } from '../lib/errors';
import { Loading } from '../ui/Page';
import { getChannelService, setChannelService } from './api';
import {
  DAY_LABELS,
  problemsWithWeek,
  summarise,
  toMinutes,
  toTime,
  windowsOf,
  windowsOn,
  type DayWindow,
} from './week';

/**
 * When this channel is attended, and what it says when it is not (context.md §45–§56). Phase 139.
 *
 * Three settings that solve one complaint: somebody writes at eleven at night, nobody answers, and
 * they decide the shop is ignoring them. Now the shop says it is closed — once, not once per
 * message — and, inside working hours, says "já vamos atender" to anybody who has been in the queue
 * too long.
 *
 * The hours default to the tenant's, which is almost always right. The override exists because the
 * same company may answer WhatsApp until ten at night and e-mail only during the day.
 */

export function ChannelServicePanel({ channelId }: { channelId: string }): React.JSX.Element {
  const client = useQueryClient();
  const service = useQuery({
    queryKey: ['channel-service', channelId],
    queryFn: () => getChannelService(channelId),
  });

  const [own, setOwn] = useState(false);
  const [timeZone, setTimeZone] = useState('America/Sao_Paulo');
  const [windows, setWindows] = useState<DayWindow[] | null>(null);
  const [away, setAway] = useState('');
  const [waiting, setWaiting] = useState('');
  const [after, setAfter] = useState('');
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!service.data) return;

    const source = service.data.serviceHours ?? service.data.tenantHours;

    setOwn(service.data.serviceHours !== null);
    setTimeZone(source.timeZone);
    setWindows(windowsOf(source));
    setAway(service.data.awayMessage ?? '');
    setWaiting(service.data.waitingMessage ?? '');
    setAfter(service.data.waitingAfterMinutes ? String(service.data.waitingAfterMinutes) : '');
  }, [service.data]);

  const save = useMutation({
    mutationFn: () =>
      setChannelService(channelId, {
        // Unticked means "follow the tenant's", which is a null rather than a copy: the day the
        // company changes its hours, a copy would quietly keep the old ones.
        serviceHours: own ? summarise(windows ?? [], timeZone) : null,
        awayMessage: away.trim() || null,
        waitingMessage: waiting.trim() || null,
        waitingAfterMinutes: after ? Number(after) : null,
      }),
    onSuccess: () => {
      setError(null);
      setSaved(true);
      void client.invalidateQueries({ queryKey: ['channel-service', channelId] });
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  if (service.isLoading || !windows) return <Loading />;

  const change = (next: DayWindow[]): void => {
    setSaved(false);
    setWindows(next);
  };

  /** A day with no windows is a day the desk is closed, which is how a day is turned off. */
  const addWindow = (day: number): void => {
    const existing = windowsOn(windows, day);
    const last = existing.at(-1);

    change([
      ...windows,
      last
        ? // A second window starts after the first closes: a break, not an overlap to correct.
          { day, startMinute: Math.min(last.endMinute + 60, 23 * 60), endMinute: 18 * 60 }
        : { day, startMinute: 9 * 60, endMinute: 18 * 60 },
    ]);
  };

  const weekProblem = own ? problemsWithWeek(windows) : null;

  return (
    <div className="stack service-hours">
      <label className="checkbox">
        <input
          type="checkbox"
          checked={own}
          onChange={(event) => {
            setOwn(event.target.checked);
            setSaved(false);
          }}
        />
        <span>{t('channels.ownHours')}</span>
      </label>

      {!own && (
        <p className="muted small">
          {t('channels.inheritHours')
            .replace('{from}', toTime(service.data!.tenantHours.startMinute))
            .replace('{to}', toTime(service.data!.tenantHours.endMinute))}
        </p>
      )}

      {/*
       * A week, day by day (Phase 141).
       *
       * The single window could not say "nove ao meio-dia e duas às seis", nor "sábado até uma" —
       * which between them describe most desks that are not offices. A day with no rows is a day
       * the desk is closed, which is also how a day is turned off.
       */}
      {own && (
        <ul className="week">
          {DAY_LABELS.map((label, day) => {
            const today = windowsOn(windows, day);

            return (
              <li key={label} className="week__day">
                <span className="week__label">{label}</span>

                <span className="week__windows">
                  {today.length === 0 && (
                    <span className="muted small">{t('channels.closed')}</span>
                  )}

                  {today.map((window, index) => (
                    <span key={`${day}-${index}`} className="week__window">
                      <input
                        className="input input--time"
                        type="time"
                        aria-label={t('channels.opensAt')}
                        value={toTime(window.startMinute)}
                        onChange={(event) =>
                          change(
                            windows.map((each) =>
                              each === window
                                ? { ...each, startMinute: toMinutes(event.target.value) }
                                : each,
                            ),
                          )
                        }
                      />

                      <span aria-hidden="true">—</span>

                      <input
                        className="input input--time"
                        type="time"
                        aria-label={t('channels.closesAt')}
                        value={toTime(window.endMinute)}
                        onChange={(event) =>
                          change(
                            windows.map((each) =>
                              each === window
                                ? { ...each, endMinute: toMinutes(event.target.value) }
                                : each,
                            ),
                          )
                        }
                      />

                      <button
                        type="button"
                        className="button button--quiet button--compact"
                        aria-label={t('channels.removeWindow')}
                        onClick={() => change(windows.filter((each) => each !== window))}
                      >
                        ×
                      </button>
                    </span>
                  ))}

                  <button
                    type="button"
                    className="button button--quiet button--compact"
                    onClick={() => addWindow(day)}
                  >
                    {today.length === 0 ? t('channels.openThisDay') : t('channels.addWindow')}
                  </button>
                </span>
              </li>
            );
          })}
        </ul>
      )}

      {weekProblem && (
        <p className="alert" role="alert">
          {t(weekProblem === 'overlap' ? 'channels.weekOverlap' : 'channels.weekBackwards')}
        </p>
      )}

      <label className="stack__field">
        <span>{t('channels.awayMessage')}</span>
        <textarea
          className="input"
          rows={3}
          maxLength={1000}
          value={away}
          placeholder={t('channels.awayHint')}
          onChange={(event) => {
            setAway(event.target.value);
            setSaved(false);
          }}
        />
        {/* Said plainly, because it is the difference between considerate and irritating. */}
        <span className="muted small">{t('channels.awayOnce')}</span>
      </label>

      <label className="stack__field">
        <span>{t('channels.waitingMessage')}</span>
        <textarea
          className="input"
          rows={2}
          maxLength={1000}
          value={waiting}
          placeholder={t('channels.waitingHint')}
          onChange={(event) => {
            setWaiting(event.target.value);
            setSaved(false);
          }}
        />
      </label>

      <label className="stack__field">
        <span>{t('channels.waitingAfter')}</span>
        <input
          className="input"
          type="number"
          min={1}
          max={1440}
          value={after}
          onChange={(event) => {
            setAfter(event.target.value);
            setSaved(false);
          }}
        />
      </label>

      {error && (
        <p className="alert" role="alert">
          {error}
        </p>
      )}

      {saved && <p className="muted small">{t('channels.serviceSaved')}</p>}

      <button
        type="button"
        className="button button--primary"
        disabled={save.isPending || weekProblem !== null}
        onClick={() => save.mutate()}
      >
        {save.isPending ? t('common.saving') : t('common.save')}
      </button>
    </div>
  );
}
